import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaChalkboardTeacher, FaPlus } from "react-icons/fa";
import ErrorPage from "../../../components/ErrorPage";
import { createTeacher } from "../api";
import { upsertCachedTeacher } from "../cache";
import { BackButton, LdsPage, StatusBadge } from "../components/Lds";
import type { AppError, TeacherFormValues } from "../types";
import { httpStatus } from "../utils";
import TeacherForm from "./TeacherForm";

import { useLeaveForm } from "../../../components/navigation/BackButton";
const initialValues: TeacherFormValues = {
  firstName: "",
  lastName: "",
  address: "",
  zipCode: "",
  city: "",
  phone: "",
  email: "",
  dateBirth: "",
  sharePhone: false,
  shareEmail: false,
  shareAddress: false,
  capabilities: ["CLASS_TEACHER"],
  subjectIds: [],
};

const TeacherNew = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const leaveForm = useLeaveForm();
  const [values, setValues] = useState<TeacherFormValues>(initialValues);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<AppError | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const teacher = await createTeacher(values);
      upsertCachedTeacher(teacher);
      leaveForm("/school/teachers");
    } catch (err) {
      setError({
        message:
          httpStatus(err) === 403
            ? t("error.403.message", {
                defaultValue: "You do not have permission.",
              })
            : t("teachers.management.create_failed", {
                defaultValue: "Failed to create teacher.",
              }),
        status: httpStatus(err),
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <ErrorPage
        message={error.message}
        status={error.status}
        onRetry={() => setError(null)}
      />
    );
  }

  return (
    <LdsPage>
      <div className="space-y-5">
        <BackButton fallback="/school/teachers" />

        <section className="relative overflow-hidden rounded-[28px] border border-violet-100 bg-gradient-to-r from-violet-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-white/10 dark:from-violet-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:px-7 sm:py-7">
          <div className="relative z-10 flex min-w-0 items-center gap-5 pr-0 lg:pr-[30%]">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-violet-200 to-violet-50 text-2xl font-bold text-violet-700 shadow-lg dark:border-slate-900 dark:from-violet-800 dark:to-violet-950 dark:text-violet-100 sm:h-24 sm:w-24">
              <FaPlus aria-hidden="true" />
            </div>

            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                {t("teachers.management.title", { defaultValue: "Teachers" })}
              </p>
              <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
                {t("teachers.new.title", { defaultValue: "New teacher" })}
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                {t("teachers.new.subtitle", {
                  defaultValue:
                    "Add a teacher and enter the information used by the school.",
                })}
              </p>
              <div className="mt-3">
                <StatusBadge tone="info">
                  <span className="inline-flex items-center gap-1.5">
                    <FaChalkboardTeacher aria-hidden="true" />
                    {t("teachers.details.teacher_role", {
                      defaultValue: "Teacher",
                    })}
                  </span>
                </StatusBadge>
              </div>
            </div>
          </div>

          <div
            className="pointer-events-none absolute inset-y-0 right-5 hidden items-center lg:flex"
            aria-hidden="true"
          >
            <FaChalkboardTeacher className="text-[110px] text-violet-200/70 dark:text-violet-800/40" />
          </div>
        </section>

        <TeacherForm
          values={values}
          onChange={setValues}
          onSubmit={handleSubmit}
          onCancel={() => navigate("/school/teachers")}
          submitting={submitting}
          submitLabel={t("teachers.new.create", {
            defaultValue: "Create teacher",
          })}
        />
      </div>
    </LdsPage>
  );
};

export default TeacherNew;
